import { Link } from 'react-router-dom';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { PublicFooter, SUPPORT_EMAIL } from '@/components/PublicFooter';

const Privacy = () => (
  <div className="min-h-screen flex flex-col">
    <header className="max-w-6xl w-full mx-auto flex items-center justify-between px-4 sm:px-8 py-6">
      <Link to="/" className="flex items-center gap-3">
        <ZitaLogo size={44} />
        <div className="flex flex-col leading-none">
          <ZitaWordmark className="text-3xl" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-zita-mocha mt-0.5">
            Powered by <span className="remod-accent font-semibold">RemodWorks</span>
          </span>
        </div>
      </Link>
      <nav className="flex items-center gap-3">
        <Link to="/login" className="text-zita-mocha text-sm hover:text-zita-espresso">
          Sign in
        </Link>
        <Link to="/register" className="zita-btn-primary text-sm">
          Start 3-day trial
        </Link>
      </nav>
    </header>

    <main className="flex-1">
      <article className="max-w-3xl mx-auto px-4 sm:px-8 py-10 space-y-6 text-zita-espresso">
        <div className="text-xs uppercase tracking-[0.18em] text-zita-mocha">Draft — pending owner + legal review</div>
        <h1 className="font-serif text-4xl">Zita — Privacy Policy</h1>
        <p className="text-sm text-zita-mocha">Last updated: February 2026 (DRAFT)</p>

        <Section title="1. Information we collect">
          Account (email, hashed password, display name), profile (ZIP, default radius),
          subscription state (via Square), scan history, saved listings, buyer vault, Ari chat history,
          and technical logs (user agent, IP for security).
        </Section>
        <Section title="2. What we do NOT collect">
          Zita does not collect or store your passwords for Facebook, OfferUp, Nextdoor, or other
          third-party marketplaces. The Zita browser extension keeps that authentication inside your
          own browser. Zita does not train unrelated AI models on your data.
          Zita never sees your credit card number — card details are tokenized directly by Square
          in your browser before ever reaching our servers.
        </Section>
        <Section title="3. How we use your information">
          To operate the service, run scans, generate AI analysis, send transactional emails, process
          billing, and secure the platform.
        </Section>
        <Section title="4. Third-party processors">
          • Square (billing) — receives your name, email, and card details directly from your browser.<br />
          • Google Maps Platform (Places, Distance, Geocoding) — receives location queries.<br />
          • OpenAI (via Emergent Universal LLM Key) — receives listing text and, for image analysis,
          listing photos, plus your Ari chat messages.<br />
          • SendGrid (transactional email) — receives your email address and the message content.<br />
          • MongoDB (managed) — stores your Zita data.
        </Section>
        <Section title="5. Data retention">
          Account and scan data is retained until you delete it. Payment records are retained per
          Square's requirements. Ari chat history is retained until you clear it.
        </Section>
        <Section title="6. Your rights">
          Access, correct, export, or delete your data at any time by emailing{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zita-cocoa hover:underline">{SUPPORT_EMAIL}</a>.
        </Section>
        <Section title="7. Security">
          Passwords are hashed with bcrypt. Session cookies are HTTPS-only. Sensitive session material
          is encrypted at rest with AES (Fernet). Marketplace passwords are never stored server-side.
        </Section>
        <Section title="8. Cookies &amp; local storage">
          Zita uses local storage for your session token. No third-party advertising cookies.
        </Section>
        <Section title="9. Children">
          Zita is not for anyone under 18.
        </Section>
        <Section title="10. Changes">
          Material changes will be communicated by email or in-app.
        </Section>
        <Section title="11. Contact">
          For privacy questions and data requests, email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zita-cocoa hover:underline">{SUPPORT_EMAIL}</a>.
        </Section>
      </article>
    </main>

    <PublicFooter />
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-serif text-2xl mt-6 mb-2">{title}</h2>
    <p className="text-sm text-zita-mocha leading-relaxed">{children}</p>
  </section>
);

export default Privacy;
